import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Typography,
  Skeleton,
  ButtonGroup,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Collapse,
} from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  axios,
  PageLayout,
  PageNames,
  configureQuery,
  LayoutComponentNames,
  getResponseErrorMessage,
  useLayoutComponentSettings,
} from '@diana/core';
import { MonacoEditor } from './MonacoEditor';
import { useActiveSchemaUri } from './useActiveSchemaUri';
import { generateSchemaValue, getPropertiesSchema, getSchemaSummary } from './Support';
import { ISchemaSummary } from './SchemaExplorerPage.types';
import { useActiveMode } from './useActiveMode';

const modes = {
  schema: 'JSON Schema',
  value: 'Value Example',
  defaults: 'Properties Defaults',
} as const;

export const SchemaExplorerPage = () => {
  const [activeUri, setActiveUri] = useActiveSchemaUri();

  const [openSections, setOpenSections] = useState<Partial<Record<string, boolean>>>({});

  const getComponentSchemasQuery = configureQuery.getComponentSchemas();
  const {
    data: componentSchemasData,
    error: componentSchemasError,
    isLoading: isLoadingComponentSchemas,
  } = useQuery({
    queryKey: getComponentSchemasQuery.key,
    queryFn: getComponentSchemasQuery.fn,
  });

  const getActivitySchemasQuery = configureQuery.getActivitySchemas();
  const {
    data: activitySchemasData,
    error: activitySchemasError,
    isLoading: isLoadingActivitySchemas,
  } = useQuery({
    queryKey: getActivitySchemasQuery.key,
    queryFn: getActivitySchemasQuery.fn,
  });

  const error = componentSchemasError || activitySchemasError;
  const isLoading = isLoadingComponentSchemas || isLoadingActivitySchemas;

  const { getAllSettings } = useLayoutComponentSettings();

  const componentSchemas = useMemo<ISchemaSummary[]>(() => {
    if (componentSchemasData) {
      return componentSchemasData
        .map((item) => getSchemaSummary(item.uri, item.displayName, 'component'))
        .sort((a, b) => {
          if (a.displayName > b.displayName) return 1;
          if (a.displayName < b.displayName) return -1;
          return 0;
        });
    }
    return [];
  }, [componentSchemasData]);

  const activitySchemas = useMemo<ISchemaSummary[]>(() => {
    if (activitySchemasData) {
      return activitySchemasData
        .map((item) => getSchemaSummary(item.uri, item.displayName, 'activity'))
        .sort((a, b) => {
          if (a.displayName > b.displayName) return 1;
          if (a.displayName < b.displayName) return -1;
          return 0;
        });
    }
    return [];
  }, [activitySchemasData]);

  const activeItem = useMemo(() => {
    const allSchemas = [...componentSchemas, ...activitySchemas];
    return allSchemas.find((item) => item.uri === activeUri) ?? null;
  }, [componentSchemas, activitySchemas, activeUri]);

  const noCodeSupportedComponents = useMemo(() => {
    const settings = getAllSettings();
    return settings.reduce<Partial<Record<LayoutComponentNames, boolean>>>((acc, [name]) => {
      return { ...acc, [name]: true };
    }, {});
  }, [getAllSettings]);

  const sections = useMemo<Record<string, ISchemaSummary[]>>(() => {
    return {
      'Component Schemas': componentSchemas,
      'Activity Schemas': activitySchemas,
    };
  }, [activitySchemas, componentSchemas]);

  useEffect(() => {
    if (activeItem) {
      for (const [sectionName, sectionItems] of Object.entries(sections)) {
        if (sectionItems.includes(activeItem)) {
          setOpenSections((prev) => {
            return prev[sectionName] ? prev : { ...prev, [sectionName]: true };
          });
          break;
        }
      }
    }
  }, [activeItem, sections]);

  return (
    <PageLayout
      name={PageNames.FLOW_BUILDER}
      leftDrawer={{
        isOpen: true,
        header: <Box p={3} />,
        content: (
          <Box>
            {isLoading ? (
              <Stack p={2} spacing={1}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} />
                ))}
              </Stack>
            ) : error ? (
              <Box p={2}>
                <Typography color="error">{getResponseErrorMessage(error)}</Typography>
              </Box>
            ) : (
              <Box>
                <List disablePadding>
                  {Object.entries(sections).map(([sectionName, items]) => {
                    const isOpen = !!openSections[sectionName];
                    return (
                      <Fragment key={sectionName}>
                        <ListItemButton
                          onClick={() => {
                            setOpenSections({ ...openSections, [sectionName]: !isOpen });
                          }}
                          divider
                        >
                          <ListItemText primary={sectionName} />
                          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </ListItemButton>
                        <Collapse in={isOpen}>
                          <List disablePadding>
                            {items.map((item) => {
                              const isNoCode =
                                item.objectType === 'component' &&
                                !!noCodeSupportedComponents[item.name as LayoutComponentNames];
                              return (
                                <ListItemButton
                                  key={item.uri}
                                  divider
                                  sx={{ pl: 4 }}
                                  selected={item === activeItem}
                                  onClick={() =>
                                    setActiveUri(item === activeItem ? null : item.uri)
                                  }
                                >
                                  <ListItemText primary={item.displayName} secondary={item.name} />
                                  {isNoCode && <Chip size="small" label="NoCode" />}
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Collapse>
                      </Fragment>
                    );
                  })}
                </List>
              </Box>
            )}
          </Box>
        ),
      }}
    >
      <Box>{!!activeItem && <Content {...activeItem} />}</Box>
    </PageLayout>
  );
};

const Content = (props: ISchemaSummary) => {
  const { uri } = props;

  const { data, error, isFetching } = useQuery({
    queryKey: ['SchemaViewer', uri],
    queryFn: async () => {
      const response = await axios.apiV4('config').get<object>(uri);
      return response.data;
    },
    gcTime: 0,
    staleTime: 0,
  });

  if (isFetching) {
    return (
      <Stack direction="row">
        <Box flex={1}>
          <Box maxWidth={350} width="90%">
            <Skeleton />
            <Skeleton width="75%" />
          </Box>
        </Box>
        <Box>
          <Skeleton variant="rounded" height={36} width={400} />
        </Box>
      </Stack>
    );
  }

  if (error) {
    return <Typography color="error">{getResponseErrorMessage(error)}</Typography>;
  }

  if (data) {
    return <SchemaViewer {...props} schema={data} />;
  }

  return <Fragment />;
};

const SchemaViewer = (props: ISchemaSummary & { schema: any }) => {
  const { uri, schema, displayName } = props;

  const monacoRef = useRef<any>(null);

  const [mode, setMode] = useActiveMode();

  const [value, setValue] = useState<string>('');

  const renderMode = () => {
    if (mode === 'schema') {
      return (
        <MonacoEditor
          language="json"
          value={JSON.stringify(schema, null, 2)}
          path={uri}
          onMount={(_editor, monaco) => {
            monacoRef.current = monaco;
            initializeMonaco(monaco);
          }}
          theme="vs-dark"
        />
      );
    }

    if (mode === 'value') {
      return (
        <MonacoEditor
          language="json"
          value={value}
          onChange={(val) => setValue(val ?? '')}
          path={uri}
          onMount={(_editor, monaco) => {
            monacoRef.current = monaco;
            initializeMonaco(monaco);
          }}
          theme="vs-dark"
        />
      );
    }

    if (mode === 'defaults') {
      return <PropertiesDefaults {...props} />;
    }

    return <Fragment />;
  };

  const initializeMonaco = useCallback(
    (monaco: any) => {
      if (mode === 'schema') {
        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: false,
          schemas: [],
        });
      }

      if (mode === 'value') {
        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: true,
          schemas: [
            {
              uri: 'https://json-schema.org/draft/2020-12/schema',
              fileMatch: [uri],
              schema: schema,
            },
          ],
        });
      }
    },
    [mode, schema, uri],
  );

  useEffect(() => {
    if (monacoRef.current) initializeMonaco(monacoRef.current);
  }, [initializeMonaco]);

  useEffect(() => {
    if (mode === 'value') {
      const sampleValue = generateSchemaValue(schema);
      setValue(JSON.stringify(sampleValue, null, 2));
    }
  }, [mode, schema]);

  return (
    <Fragment>
      <Stack direction="row">
        <Box flex={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5">{displayName}</Typography>
          </Stack>
          <Typography color="text.secondary">{uri}</Typography>
        </Box>
        <Box>
          <ButtonGroup variant="outlined">
            {Object.entries(modes).map(([key, label]) => (
              <Button
                variant={mode === key ? 'contained' : undefined}
                onClick={() => setMode(key as any)}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      </Stack>
      <Box pt={2}>{renderMode()}</Box>
    </Fragment>
  );
};

const PropertiesDefaults = (props: ISchemaSummary & { schema: any }) => {
  const { schema } = props;

  const items = useMemo(() => {
    const propertiesSchema = getPropertiesSchema(schema);

    if (propertiesSchema && typeof propertiesSchema.properties === 'object') {
      const required = Array.isArray(propertiesSchema.required) ? propertiesSchema.required : [];
      return Object.entries(propertiesSchema.properties)
        .map(([key, propertySchema]: any) => {
          return {
            key,
            required: required.includes(key),
            description: propertySchema.description,
            defaultValue: propertySchema.default,
          };
        })
        .sort((a, b) => {
          const getScore = ({ defaultValue, required }: typeof a) => {
            if (defaultValue !== undefined && required) return 3;
            if (defaultValue !== undefined) return 2;
            if (required) return 1;
            return 0;
          };

          const scoreA = getScore(a);
          const scoreB = getScore(b);

          if (scoreA < scoreB) return 1;
          if (scoreA > scoreB) return -1;
          return 0;
        });
    }
    return [];
  }, [schema]);

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Property</TableCell>
          <TableCell>Required</TableCell>
          <TableCell>Default</TableCell>
          <TableCell>Description</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => {
          return (
            <TableRow key={item.key}>
              <TableCell sx={{ verticalAlign: 'top' }}>{item.key}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                {item.required ? 'Required' : 'Optional'}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <JsonValue value={item.defaultValue} />
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>{item.description}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const JsonValue = (props: { value: any }) => {
  const { value } = props;

  if (value === undefined) {
    return <Typography color="text.secondary">undefined</Typography>;
  }

  return <pre>{JSON.stringify(value, null, 2)}</pre>;
};
